import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Pill,
  Save,
  FileWarning,
  X,
  AlertCircle,
  ArrowLeft,
  Factory,
  Ruler,
  RefreshCcw,
  Droplet,
  DollarSign,
  FileText,
  Thermometer,
} from "lucide-react";
import axiosInstance from "../../../src/api/axiosInstance";
import Authenticated from "../../layout/Authenticated";

const EditMedicine = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    generic_name: "",
    category: "",
    strength: "",
    uom: "",
    units_per_package: "",
    manufacturer: "",
    reorder_level: "",
    max_stock_level: "",
    description: "",
    storage_conditions: "",
    is_active: true,
    drug_code: "",
    hsn_code: "",
    schedule: "",
    therapeutic_class: "",
    composition: "",
    gst_percentage: "12",
    rack_number: "",
    is_refrigerated: false,
    is_prescription_required: true,
    bar_code: "",
    minimum_expiry_alert_days: "90",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("success");

  // Fetch medicine data on load
  useEffect(() => {
    const fetchMedicine = async () => {
      try {
        setIsLoading(true);
        const response = await axiosInstance.get(`/medicine/${id}`);
        const data = response.data;

        // Ensure UOM and GST percentage are properly converted to strings
        const formattedData = {
          name: data.name || "",
          generic_name: data.generic_name || "",
          category: data.category || "",
          strength: data.strength || "",
          uom: data.uom ? String(data.uom) : "",
          units_per_package: data.units_per_package?.toString() || "",
          manufacturer: data.manufacturer || "",
          reorder_level: data.reorder_level?.toString() || "",
          max_stock_level: data.max_stock_level?.toString() || "",
          description: data.description || "",
          storage_conditions: data.storage_conditions || "",
          is_active: data.is_active !== undefined ? data.is_active : true,
          // Missing fields support
          drug_code: data.drug_code || "",
          hsn_code: data.hsn_code || "",
          schedule: data.schedule || "",
          therapeutic_class: data.therapeutic_class || "",
          composition: data.composition || "",
          rack_number: data.rack_number || "",
          is_refrigerated: data.is_refrigerated !== undefined ? data.is_refrigerated : false,
          is_prescription_required: data.is_prescription_required !== undefined ? data.is_prescription_required : true,
          bar_code: data.bar_code || "",
          minimum_expiry_alert_days: data.minimum_expiry_alert_days?.toString() || "90",
          gst_percentage:
            data.gst_percentage !== null && data.gst_percentage !== undefined
              ? String(parseInt(data.gst_percentage))
              : "12",
        };

        setFormData(formattedData);
      } catch (error) {
        console.error("Error fetching medicine:", error);
        showNotification("Failed to fetch medicine data.", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchMedicine();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = "Name is required.";
    if (!formData.category) newErrors.category = "Category is required.";
    if (!formData.strength) newErrors.strength = "Strength is required.";
    if (!formData.uom) newErrors.uom = "UOM is required.";
    if (!formData.units_per_package)
      newErrors.units_per_package = "Units per package is required.";
    if (!formData.manufacturer)
      newErrors.manufacturer = "Manufacturer is required.";
    if (!formData.reorder_level)
      newErrors.reorder_level = "Reorder level is required.";
    if (formData.units_per_package && formData.units_per_package < 1)
      newErrors.units_per_package = "Units per package must be at least 1.";
    if (formData.reorder_level && formData.reorder_level < 0)
      newErrors.reorder_level = "Reorder level cannot be negative.";

    return newErrors;
  };

  const showNotification = (message, type = "success") => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showNotification("Please fill in all required fields.", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      await axiosInstance.put(`/medicine/${id}`, {
        name: formData.name,
        generic_name: formData.generic_name,
        category: formData.category,
        strength: formData.strength,
        uom: formData.uom,
        units_per_package: Number(formData.units_per_package),
        manufacturer: formData.manufacturer,
        reorder_level: Number(formData.reorder_level),
        max_stock_level: formData.max_stock_level
          ? Number(formData.max_stock_level)
          : null,
        description: formData.description,
        storage_conditions: formData.storage_conditions,
        is_active: formData.is_active,
        gst_percentage: formData.gst_percentage,
        // Include new fields in update
        drug_code: formData.drug_code,
        hsn_code: formData.hsn_code,
        schedule: formData.schedule,
        therapeutic_class: formData.therapeutic_class,
        composition: formData.composition,
        rack_number: formData.rack_number,
        is_refrigerated: formData.is_refrigerated,
        is_prescription_required: formData.is_prescription_required,
        bar_code: formData.bar_code,
        minimum_expiry_alert_days: Number(formData.minimum_expiry_alert_days),
      });

      navigate("/medicine", {
        state: { successMessage: "Medicine updated successfully!" },
      });
    } catch (error) {
      console.error("Error updating medicine:", error);
      const serverMsg = error?.response?.data?.error;
      if (serverMsg && serverMsg.toLowerCase().includes("already exists")) {
        // Map backend duplicate error to form fields
        setErrors((prev) => ({
          ...prev,
          name: "Medicine with this name and strength already exists",
          strength: "Medicine with this name and strength already exists",
        }));
        showNotification(serverMsg, "error");
      } else {
        showNotification(serverMsg || "Failed to update medicine.", "error");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Authenticated>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-10 w-32 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[...Array(12)].map((_, i) => (
                <div key={i}>
                  <div className="h-6 w-36 bg-gray-200 rounded animate-pulse mb-4"></div>
                  <div className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Authenticated>
    );
  }

  return (
    <Authenticated>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              <button
                onClick={() => navigate("/medicine")}
                className="mr-2 text-gray-500 hover:text-teal-600 transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
              Edit Medicine
            </h1>
            <p className="text-gray-500 text-sm">
              Update medicine details for: {formData.name}
            </p>
          </div>
        </div>

        {showAlert && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-start justify-between ${alertType === "success"
              ? "bg-green-50 text-green-800 border-l-4 border-green-500"
              : "bg-red-50 text-red-800 border-l-4 border-red-500"
              }`}
          >
            <div className="flex items-center">
              {alertType === "success" ? (
                <Pill size={20} className="mr-3 text-green-500" />
              ) : (
                <FileWarning size={20} className="mr-3 text-red-500" />
              )}
              <span>{alertMessage}</span>
            </div>
            <button
              onClick={() => setShowAlert(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={18} />
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400">
                    <Pill size={16} />
                  </span>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter name"
                    className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.name ? "border-red-500" : "border-gray-200"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                  )}
                </div>
              </div>

              {/* Generic Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Generic Name (e.g., “Acetaminophen” for Paracetamol)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400">
                    <Pill size={16} />
                  </span>
                  <input
                    type="text"
                    name="generic_name"
                    value={formData.generic_name}
                    onChange={handleChange}
                    placeholder="Enter generic name"
                    className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.generic_name ? "border-red-500" : "border-gray-200"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                  />
                  {errors.generic_name && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.generic_name}
                    </p>
                  )}
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400">
                    <Pill size={16} />
                  </span>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.category ? "border-red-500" : "border-gray-200"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                  >
                    <option value="">Select Category</option>
                    <option value="TABLET">Tablet</option>
                    <option value="CAPSULES">Capsules</option>
                    <option value="SYRUP">Syrup</option>
                    <option value="INJECTION">Injection</option>
                    <option value="AMPOULES">Ampoules</option>
                    <option value="CREAM">Cream</option>
                    <option value="DROPS">Drops</option>
                    <option value="INHELER">Inheler</option>
                    <option value="POWDER">Powder</option>
                    <option value="CAPTAB">Captab</option>
                    <option value="GEL">Gel</option>
                    <option value="LIQUID">Liquid</option>
                    <option value="ItemType">Item Type</option>
                    <option value="NA">NA</option>
                    <option value="VIAL">Vial</option>
                  </select>
                  {errors.category && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.category}
                    </p>
                  )}
                </div>
              </div>

              {/* Strength */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Strength <span className="text-red-500">*</span> (e.g.,
                  “500mg” for Paracetamol)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400">
                    <Droplet size={16} />
                  </span>
                  <input
                    type="text"
                    name="strength"
                    value={formData.strength}
                    onChange={handleChange}
                    placeholder="Enter strength (e.g., 500mg)"
                    className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.strength ? "border-red-500" : "border-gray-200"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                  />
                  {errors.strength && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.strength}
                    </p>
                  )}
                </div>
              </div>

              {/* UOM */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  UOM <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400">
                    <Ruler size={16} />
                  </span>
                  <select
                    name="uom"
                    value={String(formData.uom)}
                    onChange={handleChange}
                    className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.uom ? "border-red-500" : "border-gray-200"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                  >
                    <option value="">Select UOM</option>
                    <option value="Tablet">Tablet</option>
                    <option value="Capsule">Capsule</option>
                    <option value="ml">ml</option>
                    <option value="mg">mg</option>
                    <option value="g">g</option>
                    <option value="kg">kg</option>
                    <option value="Unit">Unit</option>
                    <option value="Strip">Strip</option>
                    <option value="Vial">Vial</option>
                    <option value="Ampoule">Ampoule</option>
                    <option value="Bottle">Bottle</option>
                  </select>

                  {errors.uom && (
                    <p className="text-red-500 text-xs mt-1">{errors.uom}</p>
                  )}
                </div>
              </div>

              {/* Units per Package */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Units per Package <span className="text-red-500">*</span>{" "}
                  (e.g., 10 tablets/strip)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400">
                    <Pill size={16} />
                  </span>
                  <input
                    type="number"
                    name="units_per_package"
                    value={formData.units_per_package}
                    onChange={handleChange}
                    placeholder="Enter units per package"
                    className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.units_per_package
                      ? "border-red-500"
                      : "border-gray-200"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                  />
                  {errors.units_per_package && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.units_per_package}
                    </p>
                  )}
                </div>
              </div>

              {/* Manufacturer */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manufacturer <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400">
                    <Factory size={16} />
                  </span>
                  <input
                    type="text"
                    name="manufacturer"
                    value={formData.manufacturer}
                    onChange={handleChange}
                    placeholder="Enter Manufacturer"
                    className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.manufacturer ? "border-red-500" : "border-gray-200"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                  />
                  {errors.manufacturer && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.manufacturer}
                    </p>
                  )}
                </div>
              </div>

              {/* Reorder Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reorder Level <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400">
                    <RefreshCcw size={16} />
                  </span>
                  <input
                    type="number"
                    name="reorder_level"
                    value={formData.reorder_level}
                    onChange={handleChange}
                    placeholder="Enter Reorder Level"
                    className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.reorder_level
                      ? "border-red-500"
                      : "border-gray-200"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                  />
                  {errors.reorder_level && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.reorder_level}
                    </p>
                  )}
                </div>
              </div>

              {/* Max Stock Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Stock Level
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400">
                    <RefreshCcw size={16} />
                  </span>
                  <input
                    type="number"
                    name="max_stock_level"
                    value={formData.max_stock_level}
                    onChange={handleChange}
                    placeholder="Enter Max Stock Level"
                    className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.max_stock_level
                      ? "border-red-500"
                      : "border-gray-200"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                  />
                  {errors.max_stock_level && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.max_stock_level}
                    </p>
                  )}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400">
                    <FileText size={16} />
                  </span>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter description"
                    className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.description ? "border-red-500" : "border-gray-200"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                  />
                  {errors.description && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Drug Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Drug Code (DCGI)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400">
                    <FileText size={16} />
                  </span>
                  <input
                    type="text"
                    name="drug_code"
                    value={formData.drug_code}
                    onChange={handleChange}
                    placeholder="Enter drug code"
                    className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.drug_code ? "border-red-500" : "border-gray-200"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                  />
                </div>
              </div>

              {/* HSN Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  HSN Code
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400">
                    <FileText size={16} />
                  </span>
                  <input
                    type="text"
                    name="hsn_code"
                    value={formData.hsn_code}
                    onChange={handleChange}
                    placeholder="Enter HSN code"
                    className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.hsn_code ? "border-red-500" : "border-gray-200"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                  />
                </div>
              </div>

              {/* Schedule */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Drug Schedule
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400">
                    <FileText size={16} />
                  </span>
                  <select
                    name="schedule"
                    value={formData.schedule}
                    onChange={handleChange}
                    className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.schedule ? "border-red-500" : "border-gray-200"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                  >
                    <option value="">Select Schedule</option>
                    <option value="H">Schedule H</option>
                    <option value="H1">Schedule H1</option>
                    <option value="X">Schedule X</option>
                    <option value="G">Schedule G</option>
                    <option value="OTC">OTC (Over The Counter)</option>
                    <option value="Narcotic">Narcotic</option>
                    <option value="Psychotropic">Psychotropic</option>
                  </select>
                </div>
              </div>

              {/* Therapeutic Class */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Therapeutic Class
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400">
                    <FileText size={16} />
                  </span>
                  <input
                    type="text"
                    name="therapeutic_class"
                    value={formData.therapeutic_class}
                    onChange={handleChange}
                    placeholder="e.g., Antibiotic"
                    className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.therapeutic_class ? "border-red-500" : "border-gray-200"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                  />
                </div>
              </div>

              {/* Rack Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rack Number
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400">
                    <Factory size={16} />
                  </span>
                  <input
                    type="text"
                    name="rack_number"
                    value={formData.rack_number}
                    onChange={handleChange}
                    placeholder="e.g., A1"
                    className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.rack_number ? "border-red-500" : "border-gray-200"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                  />
                </div>
              </div>

              {/* Bar Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bar Code
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400">
                    <Ruler size={16} />
                  </span>
                  <input
                    type="text"
                    name="bar_code"
                    value={formData.bar_code}
                    onChange={handleChange}
                    placeholder="Enter barcode"
                    className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.bar_code ? "border-red-500" : "border-gray-200"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                  />
                </div>
              </div>

              {/* Minimum Expiry Alert Days */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiry Alert Days
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400">
                    <RefreshCcw size={16} />
                  </span>
                  <input
                    type="number"
                    name="minimum_expiry_alert_days"
                    value={formData.minimum_expiry_alert_days}
                    onChange={handleChange}
                    placeholder="Days before expiry to alert"
                    className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.minimum_expiry_alert_days ? "border-red-500" : "border-gray-200"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                  />
                </div>

                {/* Storage Conditions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Storage Conditions (e.g., “Store below 25°C”)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-3 text-gray-400">
                      <Thermometer size={16} />
                    </span>
                    <input
                      type="text"
                      name="storage_conditions"
                      value={formData.storage_conditions}
                      onChange={handleChange}
                      placeholder="Enter storage conditions"
                      className={`w-full px-3 py-2.5 pl-10 bg-white border ${errors.storage_conditions
                        ? "border-red-500"
                        : "border-gray-200"
                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                    />
                    {errors.storage_conditions && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.storage_conditions}
                      </p>
                    )}
                  </div>
                </div>

                {/* GST Percentage */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    GST Percentage (%)
                  </label>
                  <select
                    name="gst_percentage"
                    value={String(formData.gst_percentage)}
                    onChange={handleChange}
                    className={`w-full px-3 py-2.5 bg-white border ${errors.gst_percentage ? "border-red-500" : "border-gray-200"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                  >
                    <option value="0">0%</option>
                    <option value="5">5%</option>
                    <option value="12">12%</option>
                    <option value="18">18%</option>
                    <option value="28">28%</option>
                  </select>

                  {errors.gst_percentage && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.gst_percentage}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center text-gray-500 text-sm mt-6">
                <AlertCircle size={16} className="mr-2" />
                <span>
                  All fields marked with <span className="text-red-500">*</span>{" "}
                  are required
                </span>
              </div>

              <div className="flex flex-col sm:flex-row justify-start items-stretch sm:items-center mt-6 space-y-3 sm:space-y-0 sm:space-x-3">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full sm:w-auto px-6 py-2.5 rounded-lg flex items-center justify-center ${isSubmitting
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-teal-600 text-white hover:bg-teal-700 shadow-md hover:shadow-lg transition-all duration-200"
                    }`}
                >
                  <Save size={18} className="mr-2" />
                  {isSubmitting ? "Updating..." : "Submit "}
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/medicine")}
                  className="w-full sm:w-auto px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </Authenticated>
  );
};

export default EditMedicine;
